/**
 * SWISS School Portal — mobile navigation drawer
 * ==============================================
 * The hamburger target on small screens. Shows the whole nav model
 * at once (sections plus their pages) because there's no room for
 * the top bar's section pills and the sub-rail side by side.
 *
 * Closes on navigation, on scrim tap, and on Escape.
 */

import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { X, Lock, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { schoolHasModule } from '../services/tiers';
import TierBadge from './TierBadge';
import './MobileDrawer.css';

const LOCKED_HINT = 'Not in your SWISS plan — contact My Safe Spaces to upgrade';

const MobileDrawer = ({ open, sections, onClose }) => {
  const { school, logout } = useAuth();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const schoolName = school?.shortName || school?.name || 'School Portal';

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} aria-hidden="true" />

      <div className="drawer" role="dialog" aria-modal="true" aria-label="Navigation">
        <div className="drawer-head">
          <div className="drawer-id">
            <div className="drawer-name">{schoolName}</div>
            <TierBadge tier={school?.tier} size="sm" />
          </div>
          <button
            type="button"
            className="drawer-close"
            onClick={onClose}
            aria-label="Close navigation menu"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <nav className="drawer-nav">
          {sections.map((section) => {
            const items = (section.items || []).filter(
              (i) => !(i.requiresAssessments && !school?.assessmentsEnabled)
            );

            // Leaf section — one link, no group.
            if (!section.items) {
              return (
                <NavLink
                  key={section.key}
                  to={section.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `drawer-section-link ${isActive ? 'active' : ''} ${
                      section.tone === 'danger' ? 'danger' : ''
                    }`
                  }
                >
                  {section.label}
                </NavLink>
              );
            }

            if (!items.length) return null;

            return (
              <div className="drawer-group" key={section.key}>
                <div className={`drawer-group-head ${section.tone === 'danger' ? 'danger' : ''}`}>
                  {section.label}
                </div>
                {items.map((item) => {
                  const locked = !!item.module && !schoolHasModule(school, item.module);
                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.exact}
                      onClick={onClose}
                      title={locked ? LOCKED_HINT : undefined}
                      className={({ isActive }) =>
                        `drawer-link ${isActive ? 'active' : ''} ${locked ? 'locked' : ''}`
                      }
                    >
                      <span>{item.label}</span>
                      {locked && <Lock size={12} aria-hidden="true" />}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <button type="button" className="drawer-signout" onClick={logout}>
          <LogOut size={16} aria-hidden="true" />
          Sign out
        </button>
      </div>
    </>
  );
};

export default MobileDrawer;
